
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import MfaSetup from '@/components/MfaSetup';
import Header from '@/components/Header';
import { markResellerMagicLinkEntry, setResellerSession } from '@/utils/resellerSession';

const SetupMfa = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => { 
    const storedCompanyData = sessionStorage.getItem('figabl_company_data');
    const emailFromForm = storedCompanyData ? JSON.parse(storedCompanyData)?.pocEmail : null;
  
    // Get email from various sources for first-time users
    const emailFromState = location.state?.email;
    const emailFromUser = user?.email;

    const email = emailFromState || emailFromForm || emailFromUser;
    
    if (email) {
      setUserEmail(email);
    } else {
      // If no email available and user is not authenticated, redirect to step 1
      if (!user) {
        navigate('/step/1');
        return;
      }
    }
  }, [location.state, user, navigate]);

  const handleComplete = async () => {
    // Handle reseller portal access - create session cookie
    // Fall back to sessionStorage if navigation state was lost (e.g. refresh)
    const fallbackOrgId = sessionStorage.getItem('figabl_reseller_org_id') || undefined;
    const fallbackEmail = sessionStorage.getItem('figabl_reseller_email') || undefined;
    const isResellerPortalAccess =
      location.state?.isResellerPortalAccess || !!fallbackOrgId;
    const resellerOrgId = location.state?.organisationId || fallbackOrgId;

    if (isResellerPortalAccess && resellerOrgId) {
      try {
        const email = userEmail || fallbackEmail || '';
        const phoneNumber = email
          ? sessionStorage.getItem(`figabl_mfa_phone_${email}`) || undefined
          : undefined;
        const resellerSubmissionIdentifier =
          sessionStorage.getItem('figabl_reseller_submission_identifier') ||
          sessionStorage.getItem('figabl_submission_identifier') ||
          undefined;

        const success = await setResellerSession(
          resellerOrgId,
          email,
          phoneNumber,
          true,
          resellerSubmissionIdentifier ?? undefined
        );

        if (!success) {
          console.error('[SetupMfa] Failed to create reseller session');
          return;
        }

        // Clear temporary reseller session data
        sessionStorage.removeItem('figabl_reseller_org_id');
        sessionStorage.removeItem('figabl_reseller_email');
        sessionStorage.removeItem('figabl_reseller_submission_identifier');

        markResellerMagicLinkEntry();
        navigate('/client/portal', { replace: true });
        return;
      } catch (error) {
        console.error('[SetupMfa] Error creating reseller session:', error);
        return;
      }
    }

    // For published organisation access via magic link
    if (location.state?.isPublishedOrgAccess && location.state?.organisationId) {
      navigate('/step/1', { replace: true });
      return;
    }

    // Determine the correct destination after MFA setup completion
    let redirectPath = '/step/1'; // default fallback

    if (user) {
      // For authenticated users
      if (isAdmin) {
        redirectPath = '/admin';
      } else if (location.state?.from?.pathname) {
        redirectPath = location.state.from.pathname;
      }
    } else {
      // For first-time users (no authenticated session)
      if (location.state?.from?.pathname) {
        redirectPath = location.state.from.pathname;
      } else {
        redirectPath = '/step/2';
      }
    }

    navigate(redirectPath, { replace: true });
  };

  const handleSkip = () => {    
    // For first-time users, go to step 2
    if (!user) {
      navigate('/step/2', { replace: true });
    } else if (isAdmin) {
      // Admin users shouldn't be able to skip MFA, but if they do, redirect to admin
      navigate('/admin', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto flex items-center justify-center py-8 px-4">
        <MfaSetup
          onComplete={handleComplete}
          onSkip={!user || !isAdmin ? handleSkip : undefined}
          userEmail={userEmail}
          isAltEmailEnrollment={!!location.state?.isAltEmailEnrollment}
          isResellerPortal={
            !!location.state?.isResellerPortalAccess ||
            !!sessionStorage.getItem('figabl_reseller_org_id')
          }
        />
      </main>
      
      <footer className="bg-white py-4 border-t">
        <div className="container mx-auto px-4">
          <p className="text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Figabl. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SetupMfa;
